import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { storageKeys } from '@/config/env';
import { setUnauthorizedHandler } from '@/lib/api/client';
import { can, type Permission } from './roles';
import type { AuthState, Business, LoginResponse, Profile } from './types';

/** Everything needed to put the admin back exactly where they were. */
interface ImpersonatedSession {
  token: string;
  expiresAt: number | null;
  profile: Profile | null;
  business: Business | null;
  state: AuthState | null;
  /** Shown in the banner, since the live profile is the impersonated one. */
  adminName: string | null;
}

interface AuthStore {
  token: string | null;
  /** Epoch ms. The old app stored a moment() string and parsed it on every tick. */
  expiresAt: number | null;
  profile: Profile | null;
  /** Selected store; `null` means "Global" for admins and CS. */
  business: Business | null;
  /**
   * The rest of the login response.
   *
   * Kept because two sidebar gates live on it and nothing else answers them.
   * The old app parked the whole blob in localStorage under
   * `{prefix}-admin-currentstate` and read it from four places.
   */
  state: AuthState | null;

  /**
   * The admin's own session, parked while they are signed in as somebody else.
   *
   * Non-null is the only reliable signal that impersonation is happening: once
   * the swap has happened the live `profile` is the *other* person's, with
   * their roles, so nothing about the current session says an admin is behind
   * it. Persisted, or a page reload would strand the admin as the customer
   * with no way back.
   */
  impersonating: ImpersonatedSession | null;

  signIn: (response: LoginResponse, ttlSeconds?: number) => void;
  signOut: () => void;
  startImpersonation: (response: LoginResponse, ttlSeconds?: number) => void;
  stopImpersonation: () => void;
  setBusiness: (business: Business | null) => void;
  isAuthenticated: () => boolean;
  can: (permission: Permission) => boolean;
}

/** Server-issued tokens carry no usable expiry, so the session is capped locally. */
const DEFAULT_TTL_SECONDS = 60 * 60 * 12;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      token: null,
      expiresAt: null,
      profile: null,
      business: null,
      state: null,
      impersonating: null,

      signIn: (response, ttlSeconds = DEFAULT_TTL_SECONDS) => {
        // The api client reads the raw token straight from localStorage on every
        // request, so it has to land under the shared key, not just in the store.
        localStorage.setItem(storageKeys.token, response.accessToken);
        set({
          token: response.accessToken,
          expiresAt: Date.now() + ttlSeconds * 1000,
          profile: response.state.profile,
          state: response.state,
        });
      },

      signOut: () => {
        localStorage.removeItem(storageKeys.token);
        localStorage.removeItem(storageKeys.impersonation);
        set({
          token: null,
          expiresAt: null,
          profile: null,
          business: null,
          state: null,
          // Signing out while impersonating ends both sessions. Keeping the
          // parked one would leave a token for an admin nobody is signed in as.
          impersonating: null,
        });
      },

      /**
       * Become somebody else, keeping the way back.
       *
       * The store's own session is copied aside *before* the new one lands, and
       * the selected business is cleared — the impersonated user has their own
       * stores, and carrying the admin's selection over would show them a store
       * they may not have.
       */
      startImpersonation: (response, ttlSeconds = DEFAULT_TTL_SECONDS) => {
        const current = get();
        if (!current.token) return;

        const parked: ImpersonatedSession = {
          token: current.token,
          expiresAt: current.expiresAt,
          profile: current.profile,
          business: current.business,
          state: current.state,
          adminName: current.profile
            ? [current.profile.firstName, current.profile.lastName]
                .filter(Boolean)
                .join(' ') || current.profile.email
            : null,
        };

        localStorage.setItem(storageKeys.token, response.accessToken);
        set({
          token: response.accessToken,
          expiresAt: Date.now() + ttlSeconds * 1000,
          profile: response.state.profile,
          state: response.state,
          business: null,
          impersonating: parked,
        });
      },

      /** Put the admin back. A no-op when nothing is parked. */
      stopImpersonation: () => {
        const parked = get().impersonating;
        if (!parked) return;

        localStorage.setItem(storageKeys.token, parked.token);
        set({
          token: parked.token,
          expiresAt: parked.expiresAt,
          profile: parked.profile,
          business: parked.business,
          state: parked.state,
          impersonating: null,
        });
      },

      setBusiness: (business) => set({ business }),

      isAuthenticated: () => {
        const { token, expiresAt, profile } = get();
        if (!token || !profile) return false;
        return expiresAt === null || Date.now() < expiresAt;
      },

      can: (permission) => can(get().profile?.roles, permission),
    }),
    {
      name: storageKeys.state,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        expiresAt: state.expiresAt,
        profile: state.profile,
        business: state.business,
        state: state.state,
        impersonating: state.impersonating,
      }),
      onRehydrateStorage: () => (state) => {
        // A session restored past its expiry must not appear signed in, and the
        // raw token key has to be re-seeded for the axios interceptor.
        if (!state) return;
        if (state.token && state.expiresAt && Date.now() >= state.expiresAt) {
          state.signOut();
        } else if (state.token) {
          localStorage.setItem(storageKeys.token, state.token);
        }
      },
    }
  )
);

/** A 401 anywhere in the app drops the session exactly once. */
setUnauthorizedHandler(() => {
  const { token, signOut } = useAuthStore.getState();
  if (token) signOut();
});

export const useProfile = () => useAuthStore((s) => s.profile);
export const useBusiness = () => useAuthStore((s) => s.business);
export const usePermission = (permission: Permission) =>
  useAuthStore((s) => can(s.profile?.roles, permission));
